import React, { useCallback, useEffect, useState } from 'react';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { ActivityIndicator, FlatList, View } from 'react-native';
import MyAppText from '../../../../components/MyAppText';
import { translate } from '../../../../services/translate';
import Tooltip from '../../../../components/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { Tabs } from '../../../../components/Tabs';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import { appAgentService } from '../../../../services/central-api/app-agent';
import useFilters from '../../../../components/Filter/hooks/useFilters';
import useDebounce from '../../../../components/hooks/useDebounce';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import ApplicationCard from './ApplicationCard';
import Filter from './Filters';
import { AppAgentParamList } from '../../../../typings/Params';
import Badge from '../../../../components/Badge';
import { useAuth } from '../../../../context/Auth';

export default function Candidacies({ navigation }: { navigation: Navigation; }) {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<AppAgentParamList, 'Candidacies'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<AppAgentParamList['Candidacies']>({
        ...route.params,
        beginIn: route.params.beginIn ? Number(route.params.beginIn) : undefined,
        endIn: route.params.endIn ? Number(route.params.endIn) : undefined,
    });

    const [textFilter, setTextFilter] = useState<string>(filters.textFilter ?? '');
    const debouncedFilter = useDebounce(textFilter, 750);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [applications, setApplications] = useState<Application[]>([]);
    const [currentMonthApplications, setCurrentMonthApplications] = useState<number>(0);
    const [nextMonthApplications, setNextMonthApplications] = useState<number>(0);

    async function getApplications() {
        try {
            setIsLoading(true);
            const response = await appAgentService.getApplications(filters);
            setApplications(response.applications);
            setCurrentMonthApplications(response.applicationsCount.currentMonth);
            setNextMonthApplications(response.applicationsCount.nextMonth);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            setApplications([]);
            setCurrentMonthApplications(0);
            setNextMonthApplications(0);
            getApplications();
        }
    }, [isFocused, filters]));

    useEffect(() => {
        navigation.setParams({
            ...filters,
            textFilter: textFilter !== '' ? textFilter : undefined,
        });

        setFilters(old => ({
            ...old,
            textFilter: textFilter !== '' ? textFilter : undefined,
        }));
    }, [debouncedFilter]);

    function listFooterComponent() {
        if (isLoading) {
            if (applications.length === 0) {
                return <View style={{ flex: 1, justifyContent: 'center' }}>
                    <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                </View>;
            }
            return <ActivityIndicator size='large' color={theme.colors.loadingColor} />;
        }
    }

    function listEmptyComponent() {
        if (!isLoading && applications.length === 0) {
            return (
                <View style={{ flex: 1, justifyContent: 'center' }}>
                    <MyAppText style={{ textAlign: 'center' }}>{translate('thereIsNoVacanciesInsidePeriod')}</MyAppText>
                </View>
            );
        }
    }

    function displayApplicationsCards(status: 'approved' | 'inProgress' | 'finished' | 'canceled') {
        return <View style={{ flex: 1, gap: 16 }}>
            <FlatList
                style={{ flex: 1 }}
                data={applications.filter(application => {
                    if (status === 'canceled') {
                        return application.deletedAt != null;
                    } else if (status === 'approved') {
                        return !application.deletedAt && moment().isBefore(moment(application.vacancy.activityStart));
                    } else if (status === 'inProgress') {
                        return !application.deletedAt && moment().isAfter(moment(application.vacancy.activityStart)) && moment().isBefore(moment(application.vacancy.activityFinish));
                    } else if (status === 'finished') {
                        return !application.deletedAt && moment().isAfter(moment(application.vacancy.activityFinish));
                    }

                    return true;
                }).sort((a, b) => {
                    if (['approved', 'inProgress'].includes(status)) {
                        return 1;
                    } else {
                        return moment(b.vacancy.activityStart).valueOf() - moment(a.vacancy.activityStart).valueOf();
                    }
                })}
                renderItem={(item) =>
                    <ApplicationCard
                        application={item.item}
                        onPress={(application) => {
                            if (!application.deletedAt && moment().isBefore(moment(application.vacancy.activityStart))) {
                                setIsLoading(true);
                                navigation.navigate('VacancyDetailsCancel', { id: application.id });
                            }
                        }}
                    />
                }
                keyExtractor={(item, index) => `${item}-${index}`}
                contentContainerStyle={{ flex: 1, gap: 24 }}
                ListFooterComponentStyle={{ flex: isLoading ? 1 : 0 }}
                ListFooterComponent={listFooterComponent}
                ListEmptyComponent={listEmptyComponent}
            />
        </View>;
    }

    return (
        <View style={styles.container(UnistylesRuntime.themeName == 'dark')}>
            <MyAppText style={styles.title}>{translate('myCandidacies')}</MyAppText>
            <View style={styles.contentContainer}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', zIndex: 2 }}>
                    <View style={{ flexDirection: 'column', gap: 8, zIndex: 2 }}>
                        <View style={styles.contentInfo}>
                            <MyAppText style={styles.contentInfoText}>{translate('available')}</MyAppText>
                            <Tooltip
                                useSimpleBox
                                style={{
                                    color: theme.colors.white['500'],
                                    padding: 16,
                                    borderRadius: 12,
                                    justifyContent: 'center',
                                    zIndex: 9999,
                                    background: theme.colors.neutral['500'],
                                }}
                                tooltipContent={translate('maxMonthlyDeacApplications', { deacMaxApplications: authenticatedUser?.role?.actingBody?.deacMaxApplications })}
                                tooltipDirection='right'
                            >
                                <FontAwesomeIcon icon={faCircleInfo} fontSize={14} color={theme.colors.neutral['400']} />
                            </Tooltip>
                        </View>

                        <View style={[styles.contentInfo, { zIndex: 1 }]}>
                            <Badge
                                color='gray'
                                mode='SQUARE'
                            >
                                {moment().format('MMMM').charAt(0).toUpperCase() + moment().format('MMMM').slice(1, 3)} {currentMonthApplications}/10
                            </Badge>
                            <Badge
                                color='gray'
                                mode='SQUARE'
                            >
                                {moment().add(1, 'month').format('MMMM').charAt(0).toUpperCase() + moment().add(1, 'month').format('MMMM').slice(1, 3)} {nextMonthApplications}/10
                            </Badge>
                        </View>
                    </View>
                    <Filter
                        filters={filters}
                        setFilters={setFilters}
                        setTextFilter={setTextFilter}
                        textFilter={textFilter}
                    />
                </View>

                <Tabs
                    tabs={[{
                        key: 'approved',
                        label: translate('approved'),
                    }, {
                        key: 'inProgress',
                        label: translate('inProgress'),
                    }, {
                        key: 'finished',
                        label: translate('finished'),
                    }, {
                        key: 'canceled',
                        label: translate('canceled'),
                    }]}
                >
                    {({ selectedTab }) => {
                        if (selectedTab == 'approved') {
                            return displayApplicationsCards('approved');
                        } else if (selectedTab == 'inProgress') {
                            return displayApplicationsCards('inProgress');
                        } else if (selectedTab == 'finished') {
                            return displayApplicationsCards('finished');
                        } else if (selectedTab == 'canceled') {
                            return displayApplicationsCards('canceled');
                        }
                    }}
                </Tabs>
            </View>
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: (isDark: boolean) => ({
        flex: 1,
        paddingTop: 24,
        paddingHorizontal: 16,
        gap: 24,
        backgroundColor: isDark ? 'transparent' : theme.colors.backgrounds['BG-2']
    }),
    title: {
        fontSize: 16,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    },
    contentContainer: {
        flex: 1,
        flexDirection: 'column',
        gap: 8
    },
    contentInfo: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'baseline',
        zIndex: 2
    },
    contentInfoText: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.primary['500']
    }
}));
